import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import type { AISettings } from '../../types';
import { SDR_UI_SECTIONS, buildSdrPreviewFromState } from './sdrSectionConfig';

function emptySectionRecord(): Record<string, string> {
  return Object.fromEntries(SDR_UI_SECTIONS.map(({ key }) => [key, '']));
}

interface SdrSettingsPanelProps {
  organizationId: string;
  aiSettings: AISettings;
  onSaved: (s: AISettings) => void;
  canEdit: boolean;
  busy: boolean;
  setBusy: (v: boolean) => void;
}

export const SdrSettingsPanel: React.FC<SdrSettingsPanelProps> = ({
  organizationId,
  aiSettings,
  onSaved,
  canEdit,
  busy,
  setBusy,
}) => {
  const inferMode = (s: AISettings): 'full' | 'sections' => {
    if ((s.sdrScript || '').trim()) return 'full';
    if (s.sdrSections && Object.values(s.sdrSections).some((v) => (v || '').trim())) return 'sections';
    return 'full';
  };
  const [mode, setMode] = useState<'full' | 'sections'>(() => inferMode(aiSettings));
  const [sdrScript, setSdrScript] = useState(() => (aiSettings.sdrScript || '').trim());
  const [sections, setSections] = useState<Record<string, string>>(() => ({
    ...emptySectionRecord(),
    ...aiSettings.sdrSections,
  }));

  useEffect(() => {
    setSdrScript((aiSettings.sdrScript || '').trim());
    setSections({ ...emptySectionRecord(), ...aiSettings.sdrSections });
  }, [aiSettings.sdrScript, aiSettings.sdrSections]);

  const preview = useMemo(
    () => buildSdrPreviewFromState(mode, sdrScript, sections),
    [mode, sdrScript, sections]
  );

  const handleSave = async () => {
    if (!canEdit) return;
    try {
      setBusy(true);
      const patch: Partial<AISettings> =
        mode === 'full'
          ? { sdrScript: sdrScript.trim(), sdrSections: {} }
          : { sdrScript: '', sdrSections: { ...emptySectionRecord(), ...sections } };
      const updated = await apiService.updateAISettings(organizationId, {
        ...aiSettings,
        ...patch,
      } as AISettings);
      onSaved(updated);
      alert('Roteiro SDR salvo. Aplicado na orquestração do Funil IA (POST /ai/orchestrate).');
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Erro ao salvar SDR');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ai-subtab-content sdr-settings-panel">
      <section className="settings-section">
        <h2>SDR (roteiro) — Funil IA</h2>
        <p className="section-description">
          Orienta a IA autónoma no WhatsApp (orquestração). Não se aplica às sugestões de texto para o atendente
          (copiloto). Se preencher o <strong>roteiro completo</strong>, ele tem prioridade sobre as secções.
        </p>
        <div className="sdr-mode-toggle" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className={`subtab${mode === 'full' ? ' active' : ''}`}
            onClick={() => setMode('full')}
            disabled={!canEdit}
          >
            Roteiro completo
          </button>
          <button
            type="button"
            className={`subtab${mode === 'sections' ? ' active' : ''}`}
            onClick={() => setMode('sections')}
            disabled={!canEdit}
          >
            Por secção
          </button>
        </div>
        {mode === 'full' && (
          <div className="form-group">
            <label htmlFor="sdr-script">Colar o script SDR (markdown simples ok)</label>
            <textarea
              id="sdr-script"
              className="form-textarea sdr-textarea"
              value={sdrScript}
              onChange={(e) => setSdrScript(e.target.value)}
              readOnly={!canEdit}
              rows={16}
              placeholder="SCRIPT SDR – REMARKETING&#10;OBJETIVO, TOM, fases, regras…"
            />
          </div>
        )}
        {mode === 'sections' && (
          <div className="sdr-sections-grid" style={{ display: 'grid', gap: '1rem' }}>
            {SDR_UI_SECTIONS.map(({ key, label }) => (
              <div key={key} className="form-group">
                <label htmlFor={`sdr-${key}`}>{label}</label>
                <textarea
                  id={`sdr-${key}`}
                  className="form-textarea"
                  value={sections[key] || ''}
                  onChange={(e) => setSections((p) => ({ ...p, [key]: e.target.value }))}
                  readOnly={!canEdit}
                  rows={4}
                />
              </div>
            ))}
          </div>
        )}
        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label>Pré-visualização enviada ao orquestrador (derivada)</label>
          <pre
            className="orchestrator-domain-preview"
            style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}
          >
            {preview.trim() || '(vazio)'}
          </pre>
        </div>
        {canEdit && (
          <div className="form-actions" style={{ marginTop: 12 }}>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={busy}>
              Salvar SDR
            </button>
          </div>
        )}
        {!canEdit && <p className="section-hint">Apenas dono ou administrador pode alterar este roteiro.</p>}
      </section>
    </div>
  );
};
